import { BaseResource } from '/@/modules/core/app/resources/BaseResource';
import { Route } from '/@/modules/core/app/constants/api.constants';
import { BillingPlan } from '/@/modules/billing/models/BillingPlan';
import { BillingAddon } from '/@/modules/billing/models/BillingAddon';
import { BillingSubscription } from '/@/modules/billing/models/BillingSubscription';
import { BillingCoupon } from '/@/modules/billing/models/BillingCoupon';
import { BillingInvoice } from '/@/modules/billing/models/BillingInvoice';
import { BillingPaymentSource } from '/@/modules/billing/models/BillingPaymentSource';
import { BillingCheckout } from '/@/modules/billing/models/BillingCheckout';
import { BillingCheckoutSuccess } from '/@/modules/billing/models/BillingCheckoutSuccess';
import { BillingCustomer } from '/@/modules/billing/models/BillingCustomer';
import { BillingInvoiceDownload } from '/@/modules/billing/models/BillingInvoiceDownload';
import { BillingPlanPrice } from '/@/modules/billing/models/BillingPlanPrice';

export class BillingResource extends BaseResource {
  /**
   * @returns {Promise<BillingPlan[]>}
   */
  async getAllPlans() {
    return this.getData(Route.BILLINGS_PLANS, BillingPlan);
  }

  /**
   * @returns {Promise<BillingPlan>}
   */
  async getUserPlan() {
    return this.getData(Route.BILLINGS_USER_PLAN, BillingPlan);
  }

  /**
   * @returns {Promise<BillingAddon[]>}
   */
  async getAllAddons() {
    return this.getData(Route.BILLINGS_ADDONS, BillingAddon);
  }

  /**
   * @returns {Promise<BillingPlanPrice>}
   */
  async calculateBillingPlanPrices(params) {
    return this.getData(Route.BILLINGS_CALCULATEBILLINGPLANPRICES, BillingPlanPrice, params);
  }

  /**
   * @returns {Promise<BillingCoupon[]>}
   */
  async getAllCoupons() {
    return this.getData(Route.BILLINGS_COUPONS, BillingCoupon);
  }

  /**
   * @returns {Promise<BillingSubscription>}
   */
  async getUserSubscription() {
    return this.getData(Route.BILLINGS_USER_SUBSCRIPTION, BillingSubscription, { with: ['plan'] });
  }

  /**
   * @returns {Promise<BillingInvoice[]>}
   */
  async getUserInvoices() {
    return this.getData(Route.BILLINGS_USER_INVOICES, BillingInvoice);
  }

  /**
   * @returns {Promise<BillingAddon[]>}
   */
  async getUserAddons() {
    return this.getData(Route.BILLINGS_USER_ADDONS, BillingAddon);
  }

  /**
   * @returns {Promise<BillingCoupon[]>}
   */
  async getUserCoupons() {
    return this.getData(Route.BILLINGS_USER_COUPONS, BillingCoupon);
  }

  /**
   * @returns {Promise<BillingPaymentSource[]>}
   */
  async getUserPaymentSources() {
    return this.getData(Route.BILLINGS_USER_PAYMENTSOURCES, BillingPaymentSource);
  }

  /**
   * @returns {Promise<BillingCustomer>}
   */
  async getUserCustomer() {
    return this.getData(Route.BILLINGS_USER_CUSTOMER, BillingCustomer);
  }

  /**
   * @param {BillingPaymentSource} paymentSource
   * @returns {Promise<boolean>}
   */
  async removePaymentSource(paymentSource) {
    return this.postData(Route.BILLINGS_USER_REMOVEPAYMENTMETHOD, {
      payment_source_id: paymentSource.id
    });
  }

  /**
   * @returns {Promise<BillingCheckout>}
   */
  async postCheckoutFlow(hostPageId) {
    return BillingCheckoutSuccess.mapModel(
      await this.postData(Route.BILLINGS_USER_POSTCHECKOUT, {
        host_page_id: hostPageId
      })
    );
  }

  /**
   * @returns {Promise<BillingCheckout>}
   */
  async getCheckoutUrl(planId) {
    return this.getData(Route.BILLINGS_USER_CHECKOUT_URL, BillingCheckout, {
      plan_id: planId
    });
  }

  /**
   * @returns {Promise<BillingCheckout>}
   */
  async getCheckoutExistingUrl(planId) {
    return this.getData(Route.BILLINGS_USER_CHECKOUTEXISTINGURL, BillingCheckout, {
      plan_id: planId
    });
  }

  /**
   * @returns {Promise<BillingInvoiceDownload>}
   */
  async downloadInvoice(invoiceId) {
    return this.getData(Route.BILLINGS_DOWNLOADINVOICE, BillingInvoiceDownload, {
      invoice_id: invoiceId
    });
  }
}

export default new BillingResource();
